import React, { useEffect, useState, Component }  from 'react'
import { View, StyleSheet, SectionList, TouchableOpacity, Platform } from 'react-native'
import StyledTextInput from './StyledTextInput'
import { Text, Button, CheckBox, Icon, useTheme } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { currencyFormatterAtom, productsUpdatedAtom, productUnitsDictAtom, userStoreIdAtom, productFuseSearchAtom } from '../global/AtomsProvider'
import { searchProductsByStore } from '../utils/fuseHelper'
import { isVisibleInNewSale } from '../utils/productListHelpers'
import StoreChooserHorizontal from './StoreChooserHorizontal'
import { useColors } from '../utils/useColors'
import { roundNumber } from '../utils/numberUtils'

export default function ProductList({ products, customFuse=null, onItemPress, style, showStoreFilter=false, preselectedStore=null, showAdditionallyInactiveProducts=false, allowMultipleSelection=false, selectedProducts=[], links=null, excludeHiddenInNewSale=false, onCategoryPress=null }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const colors = useColors()
  const userStoreId = useAtomValue(userStoreIdAtom)
  const [store, setStore] = useState(preselectedStore ?? userStoreId)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const productsUpdated = useAtomValue(productsUpdatedAtom)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const defaultProductUnit = t('pcs')
  const [data, setData] = useState(products)
  const [allDataByStore, setAllDataByStore] = useState(products)
  const [query, setQuery] = useState('')
  // Single shared 'all' index; store-scoping happens in searchProductsByStore (ATL-631).
  // customFuse callers pass an already-scoped index, so they skip the store filter.
  const sharedFuse = useAtomValue(productFuseSearchAtom)
  const fuse = customFuse ?? sharedFuse
  const [chosenCategory, setChosenCategory] = useState(null)
  const _sectionList = React.createRef()

  useEffect(() => {
    filterByStore(store)
    if (query.length > 1) {
      handleSearch(query)
    }
    if (chosenCategory) {
      const category = products.find(category => category.id === chosenCategory)
      if (category) {
        setData([category])
      }
    }
  }, [productsUpdated.updateCount])

  useEffect(() => {
    if (query.length > 1) {
      filterByStore(store)
      handleSearch(query)
    } else {
      filterByStore(store)
    }
  }, [store])

  const filterByStore = store => {
    if (store === 'all') {
      setData(products)
      setAllDataByStore(products)
      return
    }
    var filteredData = []
    for (const [index, value] of products.entries()) {
      let filteredValue = value.data.filter(function(product) {
        return product.activeInStore?.[store] === true
      })
      if (filteredValue.length > 0) {
        const newDict = {
          title: value.title,
          data: filteredValue
        }
        filteredData.push(newDict)
      }
    }
    if (filteredData.length == 0) {
      const newDict = {
        title: t('notFound'),
        data: [],
      }
      filteredData.push(newDict)
    }
    setData(filteredData)
    setAllDataByStore(filteredData)
  }
  
  const handleSearch = (text, fuseInstance=fuse) => {
    setQuery(text)
    if (chosenCategory) {
      setChosenCategory(null)
    }
    if (text.length === 0) {
      setData(allDataByStore)
      return
    }
    // customFuse is pre-scoped to its own product list → raw search; the shared index
    // covers all stores → scope by activeInStore post-search (ATL-631).
    const searchResults = customFuse
      ? fuseInstance.search(text)
      : searchProductsByStore(fuseInstance, text, store)
    // Map back to original product objects
    let filteredValue = searchResults.map(result => result.item)
    // ATL-1366: the browse list handed to us was already filtered for this surface (New Sale
    // drops isHiddenInNewSale products, and whole categories with them), but search does not go
    // through that list — it queries the shared all-catalogue index. Re-apply the surface's own
    // predicate here or hiding a product has no effect on the one path a waiter actually uses.
    // Applied to BOTH branches deliberately: a customFuse caller that asks for the exclusion must
    // get it, rather than silently getting a no-op because its index happens to be pre-scoped.
    if (excludeHiddenInNewSale) {
      filteredValue = filteredValue.filter(isVisibleInNewSale)
    }
    setData([{
      title: t('searchResults'),
      data: filteredValue
    }])
  }

  const renderSearchHeader = () => (
    <>
      { links && 
        <View style={{flexDirection: 'row', justifyContent: 'flex-end', gap: 10}}>
          {links.map(link => (
            <Button
              key={link.text}
              style={{marginRight: -20}}
              size='tiny'
              appearance='ghost'
              onPress={link.action}
              testID={link.testID}
              accessoryLeft={link.icon ? (props) => (<Icon name={link.icon} {...props} style={{...props.style, marginRight: 0}} size='small' />) : undefined}
            >
              {link.text}
            </Button>
          ))}
        </View>
      }
      { showStoreFilter && <StoreChooserHorizontal forceShowForSingleStoreOrgs={showAdditionallyInactiveProducts} store={store} style={{marginTop: 5}} onChange={(newStore) => {
        setStore(newStore)
      }} /> }
      <SearchHeader setData={setData} products={allDataByStore} query={query} store={store} handleSearch={handleSearch} searchWord={t('search')} allWord={t('all')} setChosenCategory={setChosenCategory} onCategoryPress={onCategoryPress} />
    </>
  )

  const renderItem = ({item, index}) => (
    <ProductRow
      item={item}
      index={index}
      store={store}
      fallbackStore={userStoreId}
      onItemPress={onItemPress}
      productsUpdated={productsUpdated}
      currencyFormatter={currencyFormatter}
      offIndicator={t('off')}
      allowMultipleSelection={allowMultipleSelection}
      isSelected={selectedProducts.some(p => p.id === item.id)}
      productUnitsDict={productUnitsDict}
      defaultProductUnit={defaultProductUnit}
      theme={theme}
      colors={colors}
    />
  )

  const renderSectionHeader = ({ section: { title } }) => (
    <SectionTitleRow title={title}/>
  )

  const renderFooter = () => (
    <View style={{height: 50}}></View>
  )

  return (
    <View style={[{flex: 1, alignItems: 'stretch'}, style ]}>
      <SectionList 
        ref={_sectionList}
        ListHeaderComponent={renderSearchHeader()}
        initialNumToRender={20}
        sections={data}
        extraData={selectedProducts}
        keyExtractor={(item, index) => item.id}
        renderItem={renderItem}
        renderSectionHeader={renderSectionHeader}
        contentContainerStyle={styles.list} 
        keyboardDismissMode='on-drag'
        keyboardShouldPersistTaps='handled'
        ListFooterComponent={renderFooter}
      />
    </View>   
  )
}

class ProductRow extends Component {
  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.productsUpdated.updateCount != nextProps.productsUpdated.updateCount) {
      return true
    }
    if (this.props.item.id !== nextProps.item.id) {
      return true;
    }
    if (this.props.item.type !== nextProps.item.type) {
      return true;
    }
    if (this.props.item.name !== nextProps.item.name) {
      return true;
    }
    if (this.props.store !== nextProps.store) {
      return true;
    }
    if (this.props.item.salePrices?.[this.props.store] !== nextProps.item.salePrices?.[nextProps.store]) {
      return true;
    }
    if (this.props.item.activeInStore?.[this.props.store] !== nextProps.item.activeInStore?.[nextProps.store]) {
      return true;
    }
    if (this.props.isSelected !== nextProps.isSelected) {
      return true;
    }
    return false;
  }

  render() {
    const { item, index, store, fallbackStore, onItemPress, currencyFormatter, offIndicator, allowMultipleSelection, isSelected, productUnitsDict, defaultProductUnit, theme, colors } = this.props;
    return (
      <TouchableOpacity
        style={[styles.item, { backgroundColor: theme['background-basic-color-1'] }, isSelected && { backgroundColor: colors.selectedLightBlue, borderLeftWidth: 4, borderLeftColor: colors.primary }]}
        onPress={() => {
          onItemPress(item, index, store)
        }}
        key={item.id}
        testID={`product-item-${item.id}`}
        {...(Platform.OS === 'web' ? { dataSet: { testid: `product-item-${item.id}` } } : {})}
      >
        <View style={styles.itemRow}>
          {allowMultipleSelection && (
            <CheckBox
              checked={isSelected}
              onChange={() => onItemPress(item, index, store)}
              style={styles.checkbox}
            />
          )}
          <View style={{flex: 1}}>
            <View style={styles.itemTitleContainer}>
              <Text style={styles.itemTitle} category='s2'>
                {item.name}
              </Text> 
              {item.activeInStore && !item.activeInStore?.[store === 'all' ? fallbackStore : store] && 
                <Text style={styles.itemOffIndicator} category='c1'>
                  {`(${offIndicator})`}
                </Text>
              }
            </View>
            <View style={styles.itemDetailsContainer}>
              <Text style={styles.itemPrice} category='c1'>
                {currencyFormatter.format(item.salePrices?.[store === 'all' ? fallbackStore : store])}
              </Text> 
              {!item.isService && (!item.isComposite || item.manufacturedBeforeSale) && store !== 'all' && <Text style={styles.itemQuantity} category='c1' status={item.storeStock?.[store] > 0 ? 'basic' : 'danger'}>
                {roundNumber(item.storeStock?.[store] || 0) + ' ' + (productUnitsDict[item.productUnit]?.shortName || defaultProductUnit)}
              </Text>}
            </View>
          </View>
        </View>
      </TouchableOpacity>
    )
  }
}

class SectionTitleRow extends Component {

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.title !== nextProps.title) {
      return true;
    }
    return false;
  }

  render() { 
    return (
      <View style={styles.sectionHeader}>
        <Text category='label'>{this.props.title}</Text>
      </View>
    )
  }
}

class SearchHeader extends Component {

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.store !== nextProps.store) {
      return true;
    }
    if (this.props.query !== nextProps.query) {
      return true;
    }
    if (this.props.products?.length !== nextProps.products?.length) {
      return true;
    }
    return false;
  }

  render() { 
    return (
      <View style={styles.listHeader}>
        <StyledTextInput autoCapitalize="none"
          autoCorrect={false}
          clearButtonMode="always"
          value={this.props.query}
          onChangeText={queryText => this.props.handleSearch(queryText)}
          placeholder={this.props.searchWord} 
          returnKeyType='done'
          style={styles.input}
          selectTextOnFocus={true}
        />
        {<View style={styles.buttonGroup}>
          <Button style={styles.button} size='tiny' appearance='ghost' 
            onPress={() => {
              this.props.setData(this.props.products)
              this.props.setChosenCategory(null)
              this.props.onCategoryPress?.()
            }}
          >
            {this.props.allWord}
          </Button>
          { this.props.products.map((item, index) => (
            <Button style={styles.button} key={item.id} size='tiny' appearance='ghost' 
              onPress={() => {
                const newData = [item]
                this.props.setData(newData)
                this.props.setChosenCategory(item.id)
                this.props.onCategoryPress?.()
              }}
            >
              {item.title}
            </Button>
          ))}
        </View>}
      </View>
    )
  }
}
  
const styles = StyleSheet.create({
  list: {
    paddingHorizontal: 20,
  },
  input: {
    marginTop: 5,
  },
  buttonGroup: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 10,
  },
  sectionHeader: {
    justifyContent: 'center',
    height: 56,
    // backgroundColor: '#FFFFFF',
  },
  item: {
    marginVertical: 2,
    paddingHorizontal: 14,
    borderRadius: 5,
    paddingVertical: 10,
  },
  listHeader: {
    // height: 200,
    justifyContent: 'center',
    // position: Platform.OS === 'web' ? 'fixed' : undefined,
    // top: Platform.OS === 'web' ?  0 : undefined,
    // zIndex: Platform.OS === 'web' ? 4 : undefined, 
    // backgroundColor: Platform.OS === 'web' ? 'white' : undefined, 
    // paddingHorizontal: Platform.OS === 'web' ? 20 : undefined,
  },
  selectedItem: {
  },
  checkbox: {
    marginRight: 10,
  },
  itemRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  itemTitleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  itemDetailsContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 7,
  },
  itemTitle: {
    flex: 1,
  },
})